import React from 'react';

import { serviceApiBase, XOMICS_WEBSERVER_ORIGIN } from './xomics-webserver-client';
import './central-api-guide.css';

function Endpoint({ method, path, children }) {
  return (
    <div className="xws-api-endpoint">
      <span className={`xws-api-method xws-api-method-${method.toLowerCase()}`}>{method}</span>
      <code>{path}</code>
      <p>{children}</p>
    </div>
  );
}

export default function CentralApiGuide({
  serviceCode,
  serviceName,
  token,
  unavailableMessage = '',
}) {
  const base = serviceApiBase(serviceCode);
  const credential = token || 'YOUR-WEBSERVER-TOKEN';

  return (
    <div className="xws-api-guide">
      <header className="xws-api-header">
        <span className="xws-api-kicker">Central XOmics contract</span>
        <h1>{serviceName} API</h1>
        <p>
          Uploads, pipeline jobs, worker execution, offload, and result files use one
          shared lifecycle. Pipeline availability and input slots come from the active
          XOmics release rather than frontend constants.
        </p>
      </header>

      {unavailableMessage && <div className="xws-api-warning">{unavailableMessage}</div>}

      <section className="xws-api-card">
        <h2>Connection</h2>
        <div className="xws-api-facts">
          <div><span>Service base</span><code>{base}</code></div>
          <div><span>Credential header</span><code>X-Webserver-Token: {credential}</code></div>
        </div>
        <p>
          Register a browser or API credential once with <code>POST {XOMICS_WEBSERVER_ORIGIN}/auth/register</code>
          {' '}and body <code>{'{ "token": "YOUR-WEBSERVER-TOKEN" }'}</code>.
          The raw credential is hashed by the backend and scopes all later job reads.
        </p>
      </section>

      <section className="xws-api-card">
        <h2>Lifecycle</h2>
        <Endpoint method="GET" path={`${base}/catalogue`}>
          Returns only this webserver&apos;s exact pipeline IDs that are online in the active
          release, plus their sample types, input slots, modes, runtime estimate, the patient
          facts each needs (<code>requires_patient_fields</code>), the report types bound to it
          (<code>report_types</code>), and <code>parameters</code>: every parameter the
          pipeline declares, with <code>supplied_by</code> naming the worker, a patient fact or
          the caller.
        </Endpoint>
        <Endpoint method="POST" path={`${base}/uploads`}>
          Declares files, the selected numeric pipeline ID, sample code, the patient facts in
          <code> patient_params</code>, caller parameters under the pipeline&apos;s own names in
          <code> pipeline_params</code>, display name, and presentation metadata. XOmics checks
          the parameters against the pipeline&apos;s declaration. The response contains XOmics
          multipart upload plans.
        </Endpoint>
        <Endpoint method="POST" path={`${base}/uploads/:uploadUuid/files/:position/assemble`}>
          Completes one declared multipart file after its signed parts have been uploaded directly.
        </Endpoint>
        <Endpoint method="POST" path={`${base}/uploads/:uploadUuid/complete`}>
          Validates all declared files and queues the exact active pipeline job.
        </Endpoint>
        <Endpoint method="GET" path={`${base}/jobs?limit=50&beforeId=:cursor`}>
          Lists only submissions owned by this credential. The cursor provides stable pagination.
        </Endpoint>
        <Endpoint method="GET" path={`${base}/uploads/:uploadUuid/jobs/:jobUuid/outputs`}>
          Lists the result files declared by the completed XOmics job.
        </Endpoint>
        <Endpoint method="GET" path={`${base}/uploads/:uploadUuid/jobs/:jobUuid/download`}>
          Returns a short-lived URL for the complete offloaded result bundle.
        </Endpoint>
        <Endpoint method="GET" path={`${base}/uploads/:uploadUuid/jobs/:jobUuid/reports/options`}>
          Lists the report types bound to the job&apos;s pipeline and the report languages.
        </Endpoint>
        <Endpoint method="POST" path={`${base}/uploads/:uploadUuid/jobs/:jobUuid/reports`}>
          Requests a report for a finished job: <code>content_type_code</code> and
          <code> language_code</code>.
        </Endpoint>
        <Endpoint method="GET" path={`${base}/uploads/:uploadUuid/jobs/:jobUuid/reports/latest`}>
          Returns the latest report&apos;s status; <code>.../reports/:reportUuid/download</code>
          signs the finished PDF.
        </Endpoint>
      </section>

      <section className="xws-api-card">
        <h2>Demo analyses</h2>
        <Endpoint method="GET" path={`${base}/demos`}>
          Lists the published demo analyses. Demo routes need no credential;
          <code> /demos/:demoUuid/outputs</code>, <code>/outputs/:outputId/download</code>,
          <code> /download</code>, <code>/reports/latest</code> and
          <code> /reports/:reportUuid/download</code> read one demo&apos;s results.
        </Endpoint>
      </section>

      <section className="xws-api-card">
        <h2>Create request shape</h2>
        <pre>{JSON.stringify({
          files: [{
            position: 1,
            original_name: 'sample.vcf.gz',
            declared_bytes: 123456,
            fills: ['PipelineName:mode:1'],
          }],
          pipelines: [123],
          sample_code: 'WB',
          patient_params: null,
          pipeline_params: { 123: {} },
          display_name: 'Sample 01',
          metadata: {},
        }, null, 2)}</pre>
        <p>
          Treat the values above as a shape example only. Always copy the exact pipeline ID,
          pipeline key, permitted sample code, and slot positions from <code>/catalogue</code>.
        </p>
      </section>
    </div>
  );
}
